import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Lightbulb, ArrowRight, Camera, FileText, Video, Phone, Search, MessageSquare, ShieldCheck } from 'lucide-react';

const useCases = [
  { Icon: Camera, title: 'Enhance and stage photos', body: 'AI brightens, declutters, removes reflections and virtually furnishes empty rooms in seconds — replacing $30/photo retouchers.', href: '/features/virtual-staging' },
  { Icon: FileText, title: 'Write listing descriptions', body: 'Generate MLS, Facebook, Instagram and Marketplace copy in one click, in 28 languages, per listing.', href: '/ai-real-estate-descriptions' },
  { Icon: Video, title: 'Produce video walkthroughs', body: 'True AI-generated cinematic clips (not just slideshows) from the same photo set.', href: '/cinematic-video' },
  { Icon: Search, title: 'Pre-qualify and value', body: 'AVMs like HouseCanary estimate value; tools like Restb.ai analyze photos for features.', href: '/best-ai-for-real-estate' },
  { Icon: MessageSquare, title: 'Answer inbound leads 24/7', body: 'AI SMS/email assistants (Structurely, Lofty) qualify buyer leads while you sleep.', href: '/best-ai-for-real-estate' },
  { Icon: Phone, title: 'Make outbound calls', body: 'AI voice tools (Air.ai, Bland) handle cold outreach at scale — use carefully and disclose.', href: '/best-ai-for-real-estate' },
];

const steps = [
  { n: 1, title: 'Pick one workflow to automate first', body: 'Listing creation is the highest-ROI starting point — it touches photos, copy, video and microsite all at once.' },
  { n: 2, title: 'Choose an all-in-one over point tools', body: 'Stitching 4–5 separate AIs together costs more and creates inconsistent output. A bundled platform like ListBright produces a coherent listing kit.' },
  { n: 3, title: 'Always review AI output', body: 'AI is fast, not infallible. Spot-check descriptions for facts (sq ft, school district) and disclose virtual staging in MLS.' },
  { n: 4, title: 'Stay compliant', body: 'NAR requires disclosure of virtually staged photos. Fair Housing rules still apply to AI-written copy — avoid protected-class language.' },
  { n: 5, title: 'Reinvest the time saved', body: 'A 10-minute listing kit frees up hours per week — spend them on showings, follow-ups and SOI calls.' },
];

const faqs = [
  { q: 'How are real estate agents actually using AI in 2026?', a: 'The most common uses are listing creation (photo enhancement, virtual staging, descriptions, video), lead qualification via SMS/email assistants, and CMA/valuation help. Listing creation is where the biggest time savings live.' },
  { q: 'Is AI replacing real estate agents?', a: 'No. AI replaces the busywork around listings and leads — it does not replace local market expertise, negotiation or relationships. Agents who adopt AI handle more listings per month with the same hours.' },
  { q: 'Do I need to disclose AI-generated photos?', a: 'Virtual staging must be disclosed per NAR guidelines and most state MLS rules. AI photo enhancement (color, brightness, decluttering) is generally accepted without disclosure as long as it faithfully represents the property.' },
  { q: 'Whats the cheapest way to start using AI for real estate?', a: 'Start with a free AI description generator (e.g. ListBright\'s) and a free virtual staging tool. When you need consistent listing output, an all-in-one at ~$15/listing pays for itself on the first deal.' },
];

export default function HowToUseAiInRealEstate() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to use AI in real estate',
      step: steps.map((s) => ({ '@type': 'HowToStep', position: s.n, name: s.title, text: s.body })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="How to Use AI in Real Estate (2026): A Practical Agent's Guide"
        description="A practical, 2026 guide to using AI in real estate — listings, leads, valuation, calls — what works, what to disclose, and which tools to start with."
        canonical="https://listbright.ai/how-to-use-ai-in-real-estate"
        breadcrumbs={[
          { label: 'Compare', path: '/compare' },
          { label: 'Best AI for Real Estate', path: '/best-ai-for-real-estate' },
          { label: 'How to use AI in real estate', path: '/how-to-use-ai-in-real-estate' },
        ]}
        jsonLd={jsonLd}
      />

      <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16 lg:py-24">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <Badge variant="secondary" className="mb-4 px-4 py-2"><Lightbulb className="w-4 h-4 mr-2" /> Practical guide</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6 leading-tight">
            How to use <span className="text-gradient">AI in real estate</span> in 2026
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
            A no-fluff guide: the six places AI actually saves agents time, the five-step rollout, and
            the disclosure rules to follow before you publish anything.
          </p>
          <Button asChild size="lg">
            <Link to="/auth">Try the all-in-one free <ArrowRight className="w-4 h-4 ml-2" /></Link>
          </Button>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container max-w-5xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-10 text-center">6 ways agents use AI today</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map(({ Icon, title, body, href }) => (
              <Link key={title} to={href} className="rounded-2xl border border-border/60 bg-card p-6 hover:border-primary/40 transition-colors">
                <Icon className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-display font-bold text-lg mb-2">{title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-muted/30 border-y border-border/40">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-10">5-step rollout</h2>
          <ol className="space-y-6">
            {steps.map((s) => (
              <li key={s.n} className="flex gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">{s.n}</div>
                <div>
                  <h3 className="font-display font-bold text-lg mb-1">{s.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container max-w-3xl mx-auto px-4">
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-8">
            <div className="flex items-start gap-3 mb-3">
              <ShieldCheck className="w-6 h-6 text-primary shrink-0" />
              <h2 className="text-xl md:text-2xl font-display font-bold">Disclosure & compliance — the short version</h2>
            </div>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Virtual staging: disclose in the MLS comments per NAR guidance.</li>
              <li>• Photo edits: minor color/brightness/decluttering is generally fine; do not remove or add structural features.</li>
              <li>• AI-written copy: still subject to Fair Housing — avoid language referencing protected classes.</li>
              <li>• AI voice calls: many states require disclosure that the caller is automated. Check local rules.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-muted/30 border-y border-border/40">
        <div className="container max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-8 text-center">FAQ</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-16">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-display font-bold mb-4">Keep exploring</h2>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild variant="outline"><Link to="/best-ai-for-real-estate">Best AI for Real Estate</Link></Button>
            <Button asChild variant="outline"><Link to="/best-ai-for-real-estate-listings">Best AI for Listings</Link></Button>
            <Button asChild variant="outline"><Link to="/ai-listing-generator">AI listing generator</Link></Button>
            <Button asChild variant="outline"><Link to="/real-estate-description-examples">Description examples</Link></Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}